import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  fetchProfile,
  fetchBalanceAll,
} from "../Services/profile/profileservice";
import { FaInfoCircle } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { useDebounce } from "../utils/useDebounce";
import { Info, PlusIcon, RefreshCcw, Wallet } from "lucide-react";

const WalletBalanceCard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shouldFetchBalance, setShouldFetchBalance] = useState(false);
  const [balance, setBalance] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch profile and trigger balance
  const fetchProfileData = async () => {
    setIsRefreshing(true); // start showing "Refreshing..."
    setLoading(true);
    try {
      const profileData = await fetchProfile(() => setShouldFetchBalance(true));
      setProfile(profileData.data);
      setShouldFetchBalance(true);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
      setTimeout(() => setIsRefreshing(false), 600); // hide after 0.6 seconds
    }
  };

  // Fetch balance (debounced)
  useDebounce(
    () => {
      if (!shouldFetchBalance) return;
      setShouldFetchBalance(false);
      fetchBalance(); // fetch balance after debounce
    },
    200,
    [shouldFetchBalance],
  );

  // Actual balance fetch
  const fetchBalance = async () => {
    try {
      const res = await fetchBalanceAll({ account_type: "Business Wallet" });
      setBalance(res?.data?.balance ?? "N/A");
    } catch (error) {
      setBalance("N/A");
      console.error("Error fetching balance:", error);
    }
  };

  // Load once on component mount
  useEffect(() => {
    fetchProfileData();
  }, []);

  const formattedBalance =
    typeof balance === "number"
      ? `₹${balance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
      : balance || "N/A";

  return (
    <div className="flex items-center justify-between gap-1 bg-white rounded-lg p-2 md:gap-2 md:p-2">
      {/* Icon */}
      {/* <div className="w-6 md:w-6 flex items-center justify-center">
        <svg viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.539915" y="6.28937" width="21" height="4" rx="1.5" transform="rotate(-4.77865 0.539915 6.28937)" fill="#7D6B9D" stroke="black" />
          <circle cx="11.5" cy="5.5" r="4.5" fill="white" stroke="blue" strokeWidth="2" />
          <path d="M2.12011 6.64507C7.75028 6.98651 12.7643 6.94947 21.935 6.58499C22.789 6.55105 23.5 7.23329 23.5 8.08585V24C23.5 24.8284 22.8284 25.5 22 25.5H2C1.17157 25.5 0.5 24.8284 0.5 24V8.15475C0.5 7.2846 1.24157 6.59179 2.12011 6.64507Z" fill="blue" stroke="black" />
          <path d="M16 13.5H23.5V18.5H16C14.6193 18.5 13.5 17.3807 13.5 16C13.5 14.6193 14.6193 13.5 16 13.5Z" fill="white" stroke="black" />
        </svg>
      </div> */}

      <div>
        <Wallet className="text-gray-800" />
      </div>

      {/* Text and Balance */}
      <div className="flex flex-col items-start justify-start w-full md:w-48">
        <span className="text-xs flex items-center justify-center gap-1 text-nowrap md:text-md text-black font-bold tracking-wider">
          <p className="">Business Wallet</p>
        </span>

        <div className="flex items-center text-sm md:text-base text-black font-semibold tracking-wide relative group">
          <span className="text-xs md:text-lg inline-block mr-1">
            {isRefreshing ? "Refreshing..." : formattedBalance}
          </span>

          {/* Info Icon */}
          <Info className="cursor-pointer ms-3 hidden sm:flex" />

          {/* Refresh Button */}
          <button
            onClick={fetchProfileData}
            className="ml-2 px-2 py-1 text-white rounded-md text-xs hover:bg-blue-500 transition"
            title="Refresh Balance"
          >
            <RefreshCcw className="text-gray-800" />
          </button>

          {/* Tooltip */}
          <div className="absolute -top-9 left-1/2 -translate-x-1/2 mb-2 w-56 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
            This balance will be used for service charges only.
          </div>
        </div>
      </div>

      {/* Add Fund Button */}
      <Link
        to="/AddFund"
        className="p-2 rounded-full bg-blue-800 text-white text-xs md:text-sm flex items-center gap-1 hover:bg-blue-500 transition"
      >
        <PlusIcon className="text-lg " />
        <p className="flex text-nowrap"> Add Fund</p>
      </Link>
    </div>
  );
};

export default WalletBalanceCard;
